import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Form, Modal, Button } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { withCookies, useCookies } from "react-cookie";
import configVars from "../config";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import jwt_decode from "jwt-decode";
import { addNewBoard, updateBoard } from "../redux/actions/boards";
import { useDispatch } from "react-redux";
import { getWithExpiry, setWithExpiry } from "../common";
import ConfirmationBox from "./ConfirmationBox";

const AddNewBoard = ({
  show,
  handleClose,
  saveNewBoard,
  selectedBoard,
  memberships,
}) => {
  const [translation] = useTranslation("translations");
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [boardGuides, setBoardGuides] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedGuide, setSelectedGuide] = useState(0);
  const [userCompany, setUserCompany] = useState(null);
  const [isCompanyOwner, setIsCompanyOwner] = useState(false);
  const [title, setTitle] = useState({
    value: "",
    error: false,
  });
  const [cookies] = useCookies(["sustainyou_user_jwt"]);

  useEffect(() => {
    if (cookies.sustainyou_user_jwt) {
      const jwtDecoded = jwt_decode(cookies.sustainyou_user_jwt);
      if (jwtDecoded) {
        getUsersList(jwtDecoded);
      }

      getBoardGuides(cookies.sustainyou_user_jwt, memberships);
    }
  }, [cookies, memberships]);
  useEffect(() => {
    if (selectedBoard) {
      setTitle({
        value: selectedBoard?.title,
        error: false,
      });

      setUsers(selectedBoard?.users);

      if (selectedBoard.guide) {
        setSelectedGuide(selectedBoard.guide);
      }
    } else {
      setTitle({
        value: "",
        error: false,
      });

      setUsers([]);

      setSelectedGuide(0);
    }
  }, [selectedBoard]);

  const getUsersList = async (jwtDecoded) => {
    try {
      const response = await fetch(
        `${configVars.apiUrl}sustainchange/v1/getcompanyusers/?userid=${jwtDecoded.id}`,
        {
          method: "GET",
          headers: {
            Authorization: `Basic ${configVars.auth}`,
            "Content-Type": "application/json",
          },
        }
      );
      const userResponse = await response.json();

      if (userResponse) {
        //console.log(userResponse);

        if (userResponse.data.members) {
          setAllUsers(userResponse.data.members);
        }

        if (userResponse.data.organization) {
          setUserCompany(userResponse.data.organization);
        }

        if (userResponse.data.is_owner || userResponse.data.is_admin) {
          setIsCompanyOwner(true);
        }
      }
    } catch (error) {}
  };

  const getBoardGuides = async (token, memberships) => {
    const savedBoardGuides = getWithExpiry("sustain_change_board_guides");

    if (savedBoardGuides) {
      setBoardGuides(savedBoardGuides);
      return;
    } else {
      try {
        const response = await fetch(
          `${configVars.apiUrl}sustainchange/v1/getmembershipcourses/?token=${token}&memberships=${memberships}`,
          {
            method: "POST",
            headers: {
              Authorization: `Basic ${configVars.auth}`,
              "Content-Type": "application/json",
            },
          }
        );

        const coursesResponse = await response.json();

        //console.log( coursesResponse );

        if (coursesResponse && coursesResponse.success === true) {
          setBoardGuides(coursesResponse.data);
          setWithExpiry("sustain_change_board_guides", coursesResponse.data);
        }
      } catch (error) {}
    }
  };

  const handleBoardSelect = (id) => {
    if (!id) return;

    setSelectedGuide(id);
  };

  const resetState = () => {
    setTitle({ value: "", error: false });
    setUsers([]);
  };
  const handleChangeTitle = (e) => {
    setTitle({ value: e.target.value, error: false });
  };

  const handleSubmit = async () => {
    if (!title?.value) {
      setTitle({ ...title, error: true });
      return;
    }

    let selectedUsers = [];
    users.forEach((user) => selectedUsers.push(user.id));

    let userSelectedGuide = selectedGuide;

    if ( ! userSelectedGuide ) {
      if ( boardGuides.length > 0 ) {
        userSelectedGuide = boardGuides[0]?.id;
      }
    }

    try {
      setLoading(true);
      let newBoard = null;
      if (selectedBoard) {
        newBoard = await dispatch(
          updateBoard({
            id: selectedBoard?.id,
            title: title.value,
            user_ids: selectedUsers,
            guide: userSelectedGuide,
          })
        );
      } else {
        newBoard = await dispatch(
          addNewBoard({
            title: title.value,
            user_ids: selectedUsers,
            guide: userSelectedGuide,
          })
        );
      }

      if (newBoard && newBoard?.data?.status_code !== 200) {
        setErrorMessage(newBoard?.data?.message);
      } else {
        resetState();
        handleClose();
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        resetState();
        handleClose();
      }}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {selectedBoard
            ? translation("board_form.update_board")
            : translation("board_form.add_board")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>{translation("board_form.title.label")}</Form.Label>
            <Form.Control
              type="text"
              placeholder={translation("board_form.title.placeHolder")}
              value={title?.value}
              onChange={handleChangeTitle}
              isInvalid={title?.error}
            />

            {title?.error && (
              <Form.Control.Feedback type="invalid">
                {translation("board_form.title.error")}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          {memberships &&
            memberships.length > 0 && (
              <>
                {boardGuides && boardGuides.length > 0 && (
                  <Form.Group className="mb-3">
                    <Form.Label>Guide</Form.Label>
                    <Form.Select
                      aria-label="Board Guide"
                      onChange={(e) => handleBoardSelect(e.target.value)}
                      value={ boardGuides.length === 1 ? boardGuides[0]?.id : selectedGuide}
                    >
                      {boardGuides.length > 1 &&
                        <option>Choose Guide</option>
                      }
                      {boardGuides.map((guide, i) => (
                        <option value={guide.id} key={i}>
                          {guide.title}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                )}

                { !memberships.includes("free") &&
                  <Form.Group style={{ marginTop: "20px" }}>
                  <Form.Label className="w-100">
                    <span>{translation("board_form.users.label")}</span>
                    {userCompany && isCompanyOwner && (
                      <Link
                        to={`/organizations/${userCompany}`}
                        className="float-lg-end"
                      >
                        {translation("add_org_members")}
                      </Link>
                    )}
                  </Form.Label>
                  <Typeahead
                    id="basic-typeahead-multiple"
                    labelKey="name"
                    multiple
                    onChange={setUsers}
                    options={allUsers}
                    placeholder={translation("board_form.users.placeHolder")}
                    selected={users}
                  />
                </Form.Group>
                }
              </>
            )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={() => {
            resetState();
            handleClose();
          }}
        >
          {translation("buttons.close")}
        </Button>
        <Button variant="primary" disabled={loading} onClick={handleSubmit}>
          {loading ? (
            <Spinner animation="border" size="sm" style={{ color: "#fff" }} />
          ) : (
            translation("buttons.save")
          )}
        </Button>
      </Modal.Footer>

      <ConfirmationBox
        show={Boolean(errorMessage)}
        handleClose={() => setErrorMessage("")}
        title={translation("error")}
        message={errorMessage}
        closeButtonText="Ok"
        size="sm"
      />
    </Modal>
  );
};

export default withCookies(AddNewBoard);
